import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PlayIcon, PauseIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
export type VideoItem = {
  src: string;
  startAt?: number;
  duration?: number;
};
type VideoSequencerProps = {
  videos: (string | VideoItem)[];
  poster: string;
  caption: string;
  className?: string;
  autoPlay?: boolean;
  showControls?: boolean;
};
export function VideoSequencer({
  videos,
  poster,
  caption,
  className = '',
  autoPlay = true,
  showControls = true
}: VideoSequencerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  const [muted, setMuted] = useState(true);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // Stable list — recomputing this every render was restarting the clip
  // (and its cut timer) whenever the page re-rendered, which read as glitching.
  const parsedVideos = useMemo(() => videos.map(v => typeof v === 'string' ? {
    src: v,
    startAt: 1.5,
    duration: 5000
  } : {
    src: v.src,
    startAt: v.startAt ?? 1.5,
    duration: v.duration ?? 5000
  }), [videos]);
  const total = parsedVideos.length;
  const hasVideos = total > 0;
  const nextIndex = hasVideos ? (currentIndex + 1) % total : 0;
  useEffect(() => {
    videoRefs.current.forEach(video => {
      if (video) video.muted = muted;
    });
  }, [muted]);

  // Start the active clip from its start point; pause everything else.
  useEffect(() => {
    if (!hasVideos) return;
    const startAt = parsedVideos[currentIndex].startAt;
    videoRefs.current.forEach((video, index) => {
      if (!video || index === currentIndex) return;
      video.pause();
    });
    const video = videoRefs.current[currentIndex];
    if (!video) return;
    const start = () => {
      if (Math.abs(video.currentTime - startAt) > 0.25) {
        video.currentTime = startAt;
      }
      if (playing) {
        video.play().catch(() => {
          // Autoplay blocked or interrupted by a fast cut — safe to ignore.
        });
      }
    };
    if (video.readyState >= 1) {
      start();
      return;
    }
    video.addEventListener('loadedmetadata', start, {
      once: true
    });
    return () => video.removeEventListener('loadedmetadata', start);
    // Only re-run when the clip changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, hasVideos]);

  // Pause / resume without rewinding.
  useEffect(() => {
    const video = videoRefs.current[currentIndex];
    if (!video) return;
    if (playing) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // Hard cut to the next clip after its duration.
  useEffect(() => {
    if (!playing || !hasVideos) return;
    const timeout = setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % total);
    }, parsedVideos[currentIndex].duration);
    return () => clearTimeout(timeout);
  }, [currentIndex, playing, hasVideos, total, parsedVideos]);
  function handleEnded() {
    setCurrentIndex(prev => (prev + 1) % total);
  }
  function togglePlay() {
    setPlaying(value => !value);
  }
  return <div className={`relative overflow-hidden bg-ink ${className}`} data-id="element-62" data-mp="3fgelpwgmuler">
      {hasVideos ? parsedVideos.map((vid, index) => {
      const isActive = index === currentIndex;
      // Only buffer the clip on screen and the one coming up, so the
      // browser isn't downloading every video at once.
      const preload = isActive || index === nextIndex ? 'auto' : 'metadata';
      return <video key={vid.src} ref={el => videoRefs.current[index] = el} src={vid.src} muted={muted} playsInline preload={preload} onEnded={handleEnded} aria-hidden={!isActive} className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${isActive ? 'z-10 opacity-100' : 'z-0 opacity-0'}`} data-id="element-63" data-mp="2aekvtytobl9" data-mpi={index} />;
    }) : <img src={poster} alt={caption} className="absolute inset-0 h-full w-full object-cover opacity-75" data-id="element-64" data-mp="7ddomnrnl3mu" />}

      {showControls && <div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-5 md:p-7" data-id="element-65" data-mp="3bt6r3j2ss5xd">
          <span className="pointer-events-auto inline-flex w-fit items-center gap-2 rounded-full bg-ink/85 px-3 py-1.5 text-[0.6rem] uppercase tracking-micro text-lilac" data-id="element-66" data-mp="36uby5ob490zj">
            <span className="h-1.5 w-1.5 rounded-full bg-lilac" data-id="element-67" data-mp="3ft73yza9092b" />
            {hasVideos ? caption : 'Reel dropping soon'}
          </span>

          {hasVideos && <div className="pointer-events-auto flex items-center gap-2 self-end" data-id="element-68" data-mp="2s1s0322tpjoy">
              <button type="button" onClick={togglePlay} aria-label={playing ? 'Pause reel' : 'Play reel'} className="rounded-full bg-lilac p-3 text-ink transition-colors hover:bg-sage" data-id="element-69" data-mp="2o2aum8necmjs">
                {playing ? <PauseIcon size={16} data-id="element-70" data-mp="241rfqiad3mx9" /> : <PlayIcon size={16} data-id="element-71" data-mp="23ybwr4dkhtqk" />}
              </button>
              <button type="button" onClick={() => setMuted(value => !value)} aria-label={muted ? 'Unmute reel' : 'Mute reel'} className="rounded-full bg-lilac p-3 text-ink transition-colors hover:bg-sage" data-id="element-72" data-mp="290kqw426f1u9">
                {muted ? <VolumeXIcon size={16} data-id="element-73" data-mp="2mpwcv6wik9n3" /> : <Volume2Icon size={16} data-id="element-74" data-mp="2mqo82gm785cu" />}
              </button>
            </div>}
        </div>}
    </div>;
}